import _ from 'lodash';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useSession } from 'next-auth/client';
import classNames from 'classnames';

import logger from '@/lib/logger';
import Layout from '@/components/layout';
import AppTemplate from '@/components/apps/AppTemplate';
import AppLoadingState from '@/components/apps/AppLoadingState';
import { getAllApps, getAppPartners, getAppCategories } from '@/lib/apps';
import { sortBy } from '@/helpers/HelperFunctions';

export default function Apps({ apps, partners, categories, locale }) {
  // Check if props are present
  if (!apps) {
    return null;
  }

  const { t } = useTranslation('apps');
  const [session, loading] = useSession();

  const [appListOwned, setAppListOwned] = useState([]);
  const [appListOther, setAppListOther] = useState([]);

  const [filteredAppListOwned, setFilteredAppListOwned] = useState([]);
  const [filteredAppListOther, setFilteredAppListOther] = useState([]);

  const [appCategories, setAppCategories] = useState(categories);
  const [appPartners, setAppPartners] = useState(partners);

  const loadingAppClasses = classNames({
    'd-none': !loading
  });

  const handleCategoryChange = (categoryId) => {
    const updatedAppCategories = appCategories.map((category) => {
      if (category.id === categoryId) {
        category.checked = !category.checked;
      }

      return category;
    });

    setAppCategories(updatedAppCategories);
  }

  const handlePartnerChange = (partnerId) => {
    const updatedAppPartners = appPartners.map((partner) => {
      if (partner.id === partnerId) {
        partner.checked = !partner.checked;
      }

      return partner;
    });

    setAppPartners(updatedAppPartners);
  }

  useEffect(() => {
    if (_.isEmpty(appListOwned) && _.isEmpty(appListOther)) return;

    // When appCategories or appPartners change, update displayed apps
    let activeCategorieIds = [];
    let activePartnerIds = [];

    _.each(appCategories, (category) => {
      if (category.checked) {
        activeCategorieIds.push(category.id);
      }
    });

    _.each(appPartners, (partner) => {
      if (partner.checked) {
        activePartnerIds.push(partner.id);
      }
    });

    function filterCategories(appList, categoryIds) {
      if (!categoryIds || _.isEmpty(categoryIds)) return appList;

      return _.filter(appList, function (app) {
        return _.includes(categoryIds, app.category?.id);
      });
    }

    function filterPartners(appList, partnerIds) {
      if (!partnerIds || _.isEmpty(partnerIds)) return appList;

      return _.filter(appList, function (app) {
        return _.includes(partnerIds, app.partner.id);
      });
    }

    setFilteredAppListOwned(filterCategories(filterPartners(appListOwned, activePartnerIds), activeCategorieIds));
    setFilteredAppListOther(filterCategories(filterPartners(appListOther, activePartnerIds), activeCategorieIds));
  }, [appListOther, appCategories, appPartners])

  useEffect(() => {
    async function getOwnedApps() {
      let ownedAppIds = [];

      function filterAppsWithActiveContracts(contracts) {
        return contracts.reduce((appIds, contract) => {
          if (contract.accepted && !dayjs().isAfter(dayjs(contract.expires))) {
            appIds.push(contract.app.id);
          }

          return appIds;
        }, []);
      }

      function isOwned(app) {
        return ownedAppIds.indexOf(app.id) >= 0;
      }

      let contracts = [];

      try {
        const res = await fetch(`${process.env.NEXT_PUBLIC_API_HOST}/api/group/contracts`);
        contracts = await res.json();

        if (_.has(contracts, 'data') && !_.isEmpty(contracts.data)) {
          contracts = contracts.data;
          ownedAppIds = filterAppsWithActiveContracts(contracts);
        }
      } catch (error) {
        logger.error(error);
      }

      let ownedAppList = [];
      let otherAppList = [];

      apps.forEach(app => {
        if (isOwned(app)) {
          app.isOwned = true;
          const contract = _.first(_.filter(contracts, { 'app': { id: app.id } }));

          app.app_url = contract?.app_url || app.app_url;
          // setAppListOwned(list => [...list, app]);
          ownedAppList.push(app);
        } else {
          app.isOwned = false;
          // setAppListOther(list => [...list, app]);
          otherAppList.push(app);
        }
      });

      setAppListOwned(ownedAppList);
      setAppListOther(otherAppList);
    }

    if (session) {
      getOwnedApps();
    } else {
      setAppListOther(apps);
      setFilteredAppListOther(apps);
    }
  }, [session?.cmsId]);

  const displayAppsLoading = () => {
    return [1, 2, 3].map((index) =>
      <AppLoadingState iteration={index} key={`loading-${index}`} />
    );
  };

  const displayLOGEXBanner = (owned) => {
    const cardClasses = classNames('col-12 col-md-6', {
      'col-xl-8': !owned
    });

    return (
      <div className={cardClasses}>
        <div className="card">
          <a href="https://logex.com/de/losungen/financial-analytics/kalkulation-padiatrie/" target="_blank">
            <img src="/img/logex/logex-kinder-versorgung.png" className="img-fluid rounded" />
          </a>
        </div>
      </div>
    );
  }

  const displayApps = (appList) => {
    if (!appList) return null;

    return appList.map((app, index) =>
      <AppTemplate app={app} locale={locale} key={index} />
    );
  };

  const displayCategoryFilter = () => {
    return appCategories.map((category, index) =>
      <div className="custom-control custom-checkbox checklist-control" key={index}>
        <input
          type="checkbox"
          className="custom-control-input"
          id={`category-${index}`}
          value={category.name}
          checked={category.checked}
          onChange={() => handleCategoryChange(category.id)}
        />
        <label className="custom-control-label" htmlFor={`category-${index}`}>
          <span className="custom-control-caption">{category.name}</span>
        </label>
      </div>
    );
  }

  const displayPartnerFilter = () => {
    return appPartners.map((partner, index) =>
      <div className="custom-control custom-checkbox checklist-control" key={index}>
        <input
          type="checkbox"
          className="custom-control-input"
          id={`partner-${index}`}
          value={partner.name}
          checked={partner.checked}
          onChange={() => handlePartnerChange(partner.id)}
        />
        <label className="custom-control-label" htmlFor={`partner-${index}`}>
          <span className="custom-control-caption">{partner.name}</span>
        </label>
      </div>
    );
  }

  const resetFilters = () => {
    const resetAppCategories = categories.map((category) => {
      return {
        ...category,
        checked: false
      }
    });

    const resetAppPartners = partners.map((partner) => {
      return {
        ...partner,
        checked: false
      }
    });

    setAppCategories(resetAppCategories);
    setAppPartners(resetAppPartners);
  }

  return (
    <Layout title={t('apps')}>
      <div className="container">
        <div className="row">
          <div className="d-none d-lg-block col-lg-3 mt-6">

            <div className="card">
              <div className="card-header">
                <h4 className="card-header-title">Filter</h4>
                <a onClick={() => resetFilters()} className="small">Alle löschen</a>
              </div>

              <div className="card-body pt-3">
                <span className="dropdown-header text-muted px-0 mb-2">Kategorien</span>
                {displayCategoryFilter()}
              </div>

              <div className="card-body border-top pt-3">
                <span className="dropdown-header text-muted px-0 mb-2">Partner</span>
                {displayPartnerFilter()}
              </div>
            </div>

          </div>
          <div className="col-12 col-lg-9">

            {session && <>
              {!_.isEmpty(filteredAppListOwned) && <>
                <div className="header-body mb-4">
                  <h1 className="header-title">{t('my-apps')}</h1>
                </div>
                <div className="row">
                  {displayLOGEXBanner(true)}
                  {displayApps(filteredAppListOwned)}
                </div>
              </>}
              {!_.isEmpty(filteredAppListOther) && <>
                <div className="header-body mb-4">
                  {!_.isEmpty(filteredAppListOwned) &&
                    <h1 className="header-title">{t('more-apps')}</h1>
                  }
                  {_.isEmpty(filteredAppListOwned) &&
                    <h1 className="header-title">{t('apps')}</h1>
                  }
                </div>
                <div className="row">
                  {displayApps(filteredAppListOther)}
                </div>
              </>}
            </>}

            {!session && !loading && <>
              <div className="header-body mb-4">
                <h1 className="header-title">{t('apps')}</h1>
              </div>
              <div className="row">
                {displayLOGEXBanner(false)}
                {displayApps(filteredAppListOther)}
              </div>
            </>}

            <div className={loadingAppClasses}>
              <div className="header-body mb-4">
                <h1 className="header-title">{t('apps')}</h1>
              </div>
              <div className="row">
                {displayAppsLoading()}
              </div>
            </div>


          </div>
        </div>
      </div>
    </Layout>
  )
}

export async function getStaticProps({ locale }) {
  let apps = await getAllApps();

  // Remove contracts
  apps = _.map(apps, (app) => {
    delete app.contracts;
    delete app.partner?.registration_number;
    return app;
  });

  // Alphabetical order
  apps.sort(sortBy('name'));

  // Move "coming soon" to end
  apps = [
    ..._.filter(apps, ['coming_soon', false]),
    ..._.filter(apps, ['coming_soon', true])
  ];

  const partners = getAppPartners(apps);
  const categories = getAppCategories(apps);

  return {
    // will be passed to the page component as props
    props: {
      apps,
      partners,
      categories,
      locale,
      ...await serverSideTranslations(locale, ['common', 'menu', 'apps'])
    },
    revalidate: 1
  }
}
